<template>
    <StepModal
        :title="modalTitle"
        ref="modal"
        @on-hide="hide"
        @submit="submit"
        @update:activeIndex="activeStepIndex = $event"
        :steps="steps"
    />
</template>
<script>
import StepModal from "../../../../../../components/new/modal/StepModal.vue";

import { httpClient } from "../../../../../../services/HttpClient";
import { defineAsyncComponent } from "vue";

const SelectProductStep = defineAsyncComponent(() => import('./assignAttributeItemSteps/SelectProductsStep.vue'));
const SelectIntegrationsStep = defineAsyncComponent(() => import('./assignAttributeItemSteps/SelectIntegrationsStep.vue'));
const SelectCurrentIntegrationAttributesStep = defineAsyncComponent(() => import('./assignAttributeItemSteps/SelectCurrentIntegrationAttributesStep.vue'));
const MatchCurrentIntegrationAttributesStep = defineAsyncComponent(() => import('./assignAttributeItemSteps/MatchCurrentIntegrationAttributesStep.vue'));
const ComparisonAttributeValuesStep = defineAsyncComponent(() => import('./assignAttributeItemSteps/ComparisonAttributeValuesStep.vue'));

export default {
    name: "AssignAttributesFromIntegrationModal",
    components: {
        StepModal
    },
    emits: ['update:table'],
    props: {
        integration: {
            type: Object
        },
        category: {
            type: Object
        }
    },
    data() {
        return {
            activeStepIndex: 0,
            productScope: 'selected',
            productUuids: [],
            totalProductsCount: 0,
            search: '',
            tableFilters: {},
            products: null,
            selectedIntegrationUuids: [],
            selectedCurrentAttributeUuids: [],
            currentIntegrationAttributes: [],
            matchedAttributeUuids: [],
            attributeMatches: {},
            selectedValues: {},
            tokenBalance: null
        }
    },
    methods: {
        show(productUuids, totalProductsCount, search, tableFilters) {
            this.productUuids = productUuids;
            this.totalProductsCount = totalProductsCount;
            this.search = search ?? '';
            this.tableFilters = tableFilters ?? {};
            this.productScope = 'selected';
            this.products = null;
            this.selectedCurrentAttributeUuids = [];
            this.currentIntegrationAttributes = [];
            this.matchedAttributeUuids = [];
            this.attributeMatches = {};
            this.tokenBalance = null;
            this.$refs.modal.show();
        },
        hide() {
            this.$refs.modal.hide();
            this.selectedValues = {};
            this.selectedIntegrationUuids = [];
            this.selectedCurrentAttributeUuids = [];
            this.currentIntegrationAttributes = [];
            this.matchedAttributeUuids = [];
            this.attributeMatches = {};
            this.tokenBalance = null;
            this.products = null;
            this.activeStepIndex = 0;
            this.$refs.modal?.reset();
        },
        async submit() {
            const { integrationUuid, categoryUuid, projectUuid } = this.$route.params;

            const mappings = [];
            for (const attribute of Object.values(this.selectedValues)) {
                for (const value of Object.values(attribute.values)) {
                    const targetValueUuids = Object.values(value.currentIntegrationValue).flatMap(vals => vals.map(v => v.uuid));
                    if (targetValueUuids.length > 0) {
                        mappings.push({
                            source_value_uuid: value.uuid,
                            target_value_uuids: targetValueUuids
                        });
                    }
                }
            }

            let request = {
                mappings,
                integration_uuids: this.selectedIntegrationUuids
            };

            if (this.productScope === 'selected') {
                request.uuids = this.productUuids;
            } else {
                request = {
                    ...request,
                    ...this.tableFilters,
                    search: this.search
                };
            }

            await httpClient.post(
                `/projects/${projectUuid}/pim/channels/${integrationUuid}/categories/${categoryUuid}/publications/apply-values`,
                request
            );

            this.$refs.modal.isLoading = false;
            this.$emit('update:table');
            this.hide();
        },
    },
    computed: {
        modalTitle() {
            const base = `<span>${this.$t('assignAttributeItemsBasedOnExampleIntegrationModal.modalTitle')}</span>`;
            if (this.activeStepIndex === 0) {
                return base;
            }
            const count = this.productScope === 'selected' ? this.productUuids.length : this.totalProductsCount;
            return `${base}<span class='text-muted ml-2'>${this.$t('assignAttributeItemsBasedOnExampleIntegrationModal.modalProductsCount', { count })}</span>`;
        },
        steps() {
            return [
                {
                    name: this.$t('assignAttributeItemsBasedOnExampleIntegrationModal.steps.products'),
                    component: SelectProductStep,
                    size: '',
                    description: ` — ${this.$t('assignAttributeItemsBasedOnExampleIntegrationModal.steps.productsDescription')}`,
                    props: {
                        productScope: this.productScope,
                        'onUpdate:productScope': (value) => { this.productScope = value },
                        totalProductsCount: this.totalProductsCount,
                        productUuids: this.productUuids,
                    }
                },
                {
                    name: this.$t('assignAttributeItemsBasedOnExampleIntegrationModal.steps.selectIntegrations'),
                    component: SelectIntegrationsStep,
                    size: '',
                    description: ` — ${this.$t('assignAttributeItemsBasedOnExampleIntegrationModal.steps.selectIntegrationsDescription')}`,
                    props: {
                        selectedIntegrationUuids: this.selectedIntegrationUuids,
                        'onUpdate:selectedIntegrationUuids': (value) => { this.selectedIntegrationUuids = value },
                        'onUpdate:isReadyForNext': () => { this.$refs.modal?.refreshIsReadyForNext() }
                    }
                },
                {
                    name: this.$t('assignAttributeItemsBasedOnExampleIntegrationModal.steps.selectCurrentAttributes'),
                    component: SelectCurrentIntegrationAttributesStep,
                    size: 'lg',
                    description: ` — ${this.$t('assignAttributeItemsBasedOnExampleIntegrationModal.steps.selectCurrentAttributesDescription')}`,
                    props: {
                        productScope: this.productScope,
                        productUuids: this.productUuids,
                        totalProductsCount: this.totalProductsCount,
                        search: this.search,
                        tableFilters: this.tableFilters,
                        selectedAttributeUuids: this.selectedCurrentAttributeUuids,
                        'onUpdate:selectedAttributeUuids': (value) => { this.selectedCurrentAttributeUuids = value },
                        currentIntegrationAttributes: this.currentIntegrationAttributes,
                        'onUpdate:currentIntegrationAttributes': (value) => { this.currentIntegrationAttributes = value },
                        'onUpdate:isReadyForNext': () => { this.$refs.modal?.refreshIsReadyForNext() }
                    }
                },
                {
                    name: this.$t('assignAttributeItemsBasedOnExampleIntegrationModal.steps.matchCurrentAttributes'),
                    component: MatchCurrentIntegrationAttributesStep,
                    size: 'full',
                    description: ` — ${this.$t('assignAttributeItemsBasedOnExampleIntegrationModal.steps.matchCurrentAttributesDescription')}`,
                    props: {
                        productScope: this.productScope,
                        productUuids: this.productUuids,
                        totalProductsCount: this.totalProductsCount,
                        search: this.search,
                        tableFilters: this.tableFilters,
                        selectedIntegrationUuids: this.selectedIntegrationUuids,
                        selectedAttributeUuids: this.selectedCurrentAttributeUuids,
                        currentIntegrationAttributes: this.currentIntegrationAttributes,
                        matchedAttributeUuids: this.matchedAttributeUuids,
                        'onUpdate:matchedAttributeUuids': (value) => { this.matchedAttributeUuids = value },
                        attributeMatches: this.attributeMatches,
                        'onUpdate:attributeMatches': (value) => { this.attributeMatches = value },
                        selectedValues: this.selectedValues,
                        'onUpdate:selectedValues': (value) => { this.selectedValues = value },
                        tokenBalance: this.tokenBalance,
                        'onUpdate:tokenBalance': (value) => { this.tokenBalance = value },
                        'onUpdate:isReadyForNext': () => { this.$refs.modal?.refreshIsReadyForNext() }
                    }
                },
                {
                    name: this.$t('assignAttributeItemsBasedOnExampleIntegrationModal.steps.compareAttributes'),
                    component: ComparisonAttributeValuesStep,
                    size: 'full',
                    description: ` — ${this.$t('assignAttributeItemsBasedOnExampleIntegrationModal.steps.compareAttributesDescription')}`,
                    props: {
                        selectedValues: this.selectedValues,
                        'onUpdate:selectedValues': (value) => { this.selectedValues = value },
                        currentIntegrationData: this.currentIntegrationAttributes,
                        selectedCurrentAttributeUuids: this.selectedCurrentAttributeUuids,
                        tokenBalance: this.tokenBalance,
                        'onUpdate:tokenBalance': (value) => { this.tokenBalance = value },
                        category: this.category,
                        integration: this.integration,
                    }
                }
            ]
        }
    }
}
</script>
